import React, { useState, useRef, useCallback } from 'react';
import RichTextDisplay from './RichTextDisplay';

interface ResizableTextDisplayProps {
  content: string;
  minHeight?: number;
  maxHeight?: number;
  defaultHeight?: number;
  className?: string;
}

const ResizableTextDisplay: React.FC<ResizableTextDisplayProps> = ({
  content,
  minHeight = 32,
  maxHeight = 500,
  defaultHeight = 80,
  className = '',
}) => {
  const [height, setHeight] = useState(defaultHeight);
  const [isResizing, setIsResizing] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const startYRef = useRef(0);
  const startHeightRef = useRef(0);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
    startYRef.current = e.clientY;
    startHeightRef.current = height;

    const handleMouseMove = (e: MouseEvent) => {
      const deltaY = e.clientY - startYRef.current;
      const newHeight = Math.min(maxHeight, Math.max(minHeight, startHeightRef.current + deltaY));
      setHeight(newHeight);
    };

    const handleMouseUp = () => {
      setIsResizing(false);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, [height, minHeight, maxHeight]);

  return (
    <div className={`relative ${className}`}>
      <div
        ref={containerRef}
        className={`w-full px-3 py-2 border border-gray-300 rounded bg-gray-100 text-gray-900 text-sm overflow-auto ${
          isResizing ? 'select-none' : ''
        }`}
        style={{ height: `${height}px` }}
      >
        <RichTextDisplay content={content} />
      </div>
      {/* Resize handle */}
      <div
        className="absolute bottom-0 left-0 right-0 h-3 cursor-ns-resize flex items-center justify-center group hover:bg-gray-200 rounded-b transition-colors"
        onMouseDown={handleMouseDown}
        title="Drag to resize"
      >
        <div className="w-10 h-1 bg-gray-400 rounded group-hover:bg-gray-500 transition-colors" />
      </div>
    </div>
  );
};

export default ResizableTextDisplay;
